import React, { useEffect, useRef, useState } from "react";
import { Bluetooth, ChevronRight, Eye, Heart, Loader2, SlidersHorizontal } from "lucide-react";
import { sendBleCommand } from "../../services/api";
import { sendSettingsCmd } from "../../services/settingsChannel";
import { useToast } from "../../contexts/ToastContext";
import DetailSheet from "../session/generate/DetailSheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { DeleteConfirm, Field } from "../profile/ProfileParts";
import DeviceLiveView from "./DeviceLiveView";
import TimerFirmware, { ReleaseHistory } from "./TimerFirmware";
import TimerSettingsPanel from "./TimerSettingsPanel";
import { ShareTimerView, TimerShares, useTimerShares } from "./TimerSharing";
import { formatLastSeen } from "./settingsSummaries";

const VIEW_TITLES = {
  live: "Watch the display",
  settings: "Timer settings",
  releases: "Release history",
  share: "Share with a mate",
};

const LinkRow = ({ Icon, label, value, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="grid w-full grid-cols-[1.25rem_auto_minmax(0,1fr)_1rem] items-center gap-2.5 px-3 py-3 text-left transition-colors hover:bg-muted/30"
  >
    <Icon className="h-[18px] w-[18px] text-muted-foreground" />
    <span className="whitespace-nowrap text-sm font-medium">{label}</span>
    <span className="truncate text-right text-[13px] text-muted-foreground">{value}</span>
    <ChevronRight className="h-4 w-4 text-muted-foreground/70" />
  </button>
);

/**
 * Everything about one linked Khronos Timer, in one sheet (issue #86): its
 * status, name, firmware, the heart-rate strap it pairs itself, and — one
 * level down — the live display, its own settings and the release history.
 * The name saves when the sheet closes; unlinking asks first.
 */
const TimerSheet = ({ deviceId, timers, onClose }) => {
  const { showToast } = useToast();
  const live = timers.devices.find((d) => d.id === deviceId) || null;

  // Keep the last timer's content while the sheet animates closed.
  const [shown, setShown] = useState(live);
  useEffect(() => {
    if (live) setShown(live);
  }, [live]);
  const device = live || shown;

  const [view, setView] = useState("main");
  const [name, setName] = useState("");
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [commanding, setCommanding] = useState(false);
  const [strapNote, setStrapNote] = useState(null);
  const sharing = useTimerShares(deviceId);

  useEffect(() => {
    if (deviceId == null) return;
    setView("main");
    setConfirmUnlink(false);
    setStrapNote(null);
  }, [deviceId]);
  useEffect(() => {
    if (deviceId != null) setName(live?.name || "");
    // Only when a sheet opens: a rename landing must not reset the draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const pending = useRef({});
  pending.current = { name, device: live };

  const close = () => {
    const { name: draft, device: d } = pending.current;
    const trimmed = draft.trim();
    if (d && trimmed && trimmed !== d.name) {
      timers.rename(d, trimmed).catch((err) =>
        showToast({ title: "Couldn't rename the timer", description: err.message }),
      );
    }
    onClose();
  };

  if (!device) return null;
  const open = deviceId != null && !!live;
  const isOnline = timers.online[device.id] === true;
  const heartbeat = timers.heartbeat[device.id] || null;
  const hrmConnected = heartbeat?.hr_bpm != null;

  const strapCommand = async (command) => {
    setCommanding(true);
    setStrapNote(null);
    // Prefer the settings channel (issue #50): the command lands on the
    // device immediately and the ack confirms it acted. Firmware without the
    // channel (pre-KhronosFirmware#52) never acks — fall back to the legacy
    // pending-config HTTP path it still polls.
    try {
      await sendSettingsCmd(device.id, command === "scan" ? "ble_scan" : "ble_forget", { timeoutMs: 3000 });
      setStrapNote(command === "scan" ? "Scanning. Check the timer's screen." : "Strap forgotten.");
      setCommanding(false);
      return;
    } catch {
      /* no settings channel (or offline) — use the queued HTTP path */
    }
    try {
      await sendBleCommand(device.id, command);
      setStrapNote(
        command === "scan"
          ? "Scan sent. Check the timer's screen."
          : "Forget sent. It applies when the timer next checks in.",
      );
    } catch (err) {
      setStrapNote(err.message || "The timer didn't take the command");
    } finally {
      setCommanding(false);
    }
  };

  const unlink = async () => {
    setUnlinking(true);
    try {
      await timers.unlink(device);
      showToast({ title: `${device.name} unlinked` });
      onClose();
    } catch (err) {
      showToast({ title: "Couldn't unlink the timer", description: err.message });
    } finally {
      setUnlinking(false);
      setConfirmUnlink(false);
    }
  };

  const main = (
    <>
      <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-background p-3 text-sm">
        <span className="flex min-w-0 items-center gap-2">
          <span
            className={`h-2 w-2 shrink-0 rounded-full ${isOnline ? "bg-green-500" : "bg-muted-foreground/60"}`}
          />
          <span className="font-semibold">
            {device.in_use_by ? `In use by ${device.in_use_by}` : isOnline ? "Online" : "Offline"}
          </span>
          {!isOnline && !device.in_use_by && (
            <span className="truncate text-muted-foreground">
              · last seen {formatLastSeen(device.last_seen_at).toLowerCase()}
            </span>
          )}
        </span>
        {device.has_pending_config && device.pending_config_type !== "ota_update" && (
          <span className="shrink-0 text-xs text-muted-foreground">Change waiting</span>
        )}
      </div>

      <Field label="Name" htmlFor="timer-name" hint="Saves when you close this sheet.">
        <Input id="timer-name" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
      </Field>

      <TimerShares sharing={sharing} onAdd={() => setView("share")} />

      <TimerFirmware
        device={device}
        latest={timers.latestFor(device)}
        minRemoteVersion={timers.minRemoteVersion}
        online={isOnline}
        onChanged={timers.refresh}
        releaseCount={timers.releasesFor(device).length}
        onHistory={() => setView("releases")}
      />

      <Field
        label="Heart-rate strap"
        hint="The timer pairs the strap itself and reads your heart rate during sessions on it."
      >
        <div className="grid gap-3 rounded-xl border border-border bg-background p-3">
          {hrmConnected ? (
            <div className="flex items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-2 text-sm font-semibold">
                <Heart className="h-4 w-4 shrink-0 text-red-500" />
                <span className="truncate">{heartbeat.ble_hrm?.name || "Strap"}</span>
              </span>
              <span className="font-mono text-sm tabular-nums">{heartbeat.hr_bpm} bpm</span>
            </div>
          ) : (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Bluetooth className="h-4 w-4 shrink-0" />
              {isOnline
                ? "Its reading shows here during a session on the timer."
                : "Turn the timer on to pair a strap."}
            </p>
          )}
          {isOnline && (
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => strapCommand("scan")} disabled={commanding}>
                {commanding && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                {hrmConnected ? "Pair another" : "Pair a strap"}
              </Button>
              {hrmConnected && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => strapCommand("forget")}
                  disabled={commanding}
                  className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  Forget
                </Button>
              )}
            </div>
          )}
          {strapNote && <p className="text-xs text-muted-foreground">{strapNote}</p>}
        </div>
      </Field>

      <Field label="On the timer">
        <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-background">
          <LinkRow Icon={Eye} label="Watch the display" value={isOnline ? "Live" : "Offline"} onClick={() => setView("live")} />
          <LinkRow
            Icon={SlidersHorizontal}
            label="Timer settings"
            value="Display, clock, sound, smart home"
            onClick={() => setView("settings")}
          />
        </div>
      </Field>

      <DeleteConfirm
        action="Unlink"
        name={device.name}
        consequence="It stops getting your sessions, and mates you shared it with lose it too. To use it again, link it with a new code from its screen."
        confirming={confirmUnlink}
        onAsk={() => setConfirmUnlink(true)}
        onCancel={() => setConfirmUnlink(false)}
        onConfirm={unlink}
        busy={unlinking}
      />
    </>
  );

  return (
    <DetailSheet
      open={open}
      onOpenChange={(o) => !o && close()}
      title={view === "main" ? device.name : VIEW_TITLES[view]}
      onBack={view === "main" ? undefined : () => setView("main")}
    >
      {view === "main" && main}
      {view === "live" && (
        <>
          {isOnline ? (
            <DeviceLiveView deviceId={device.id} />
          ) : (
            <p className="rounded-xl border border-border bg-background p-3 text-sm text-muted-foreground">
              {device.name} is offline. Its screen shows here once it&rsquo;s back online.
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            Readings show while a session runs on the timer. Sound plays on the timer, not on this
            phone.
          </p>
        </>
      )}
      {view === "settings" && <TimerSettingsPanel device={device} />}
      {view === "share" && (
        <ShareTimerView device={device} sharing={sharing} onDone={() => setView("main")} />
      )}
      {view === "releases" && <ReleaseHistory releases={timers.releasesFor(device)} devices={timers.devices} />}
    </DetailSheet>
  );
};

/** Link a new timer with the 6-character code its screen shows. */
export const LinkTimerSheet = ({ open, timers, onClose }) => {
  const { showToast } = useToast();
  const [code, setCode] = useState("");
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setCode("");
      setError(null);
    }
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    if (code.length !== 6 || linking) return;
    setLinking(true);
    setError(null);
    try {
      const device = await timers.link(code);
      showToast({ title: `Linked ${device?.name || "your timer"}` });
      onClose();
    } catch (err) {
      setError(err.message || "That code didn't work. Check the timer's screen and try again.");
    } finally {
      setLinking(false);
    }
  };

  return (
    <DetailSheet
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title="Link a Khronos Timer"
      footer={
        <Button type="button" className="h-12 w-full text-base font-semibold" onClick={submit} disabled={code.length !== 6 || linking}>
          {linking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Link
        </Button>
      }
    >
      <form onSubmit={submit}>
        <Field
          label="Link code"
          htmlFor="link-code"
          hint="On the timer, open Settings › Link. It shows a 6-character code."
        >
          <Input
            id="link-code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
            placeholder="A3K9F2"
            maxLength={6}
            autoComplete="off"
            className="h-14 text-center font-mono text-2xl uppercase tracking-[0.5em]"
          />
        </Field>
      </form>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </DetailSheet>
  );
};

export default TimerSheet;
